import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AlertCircle, Printer } from 'lucide-react';
import { Button } from '../../../foundation/altzero_base/base/components/ui/button';
import { ntaService } from '../services/ntaService';
import { NtaReport, type NtaEnergyManager } from '../components/nta/NtaReport';
import { NewBadge } from '../components/NewBadge';
import { fmtMoney, ntaPeriodLabel, ntaReportFilename, type NtaResultRow, type NtaRun } from '../lib/nta';
import { mixedNote, planClientReport } from '../lib/ntaClientReport';

// Client NTA report preview: one customer, one run, the customer-level saving
// summed over its NMIs. When no NMI has a saving, the no-recommendation version
// prints instead (D-06 draft); a mixed customer gets the saving report plus a
// note naming the NMIs with no change recommended. Print → Save as PDF uses the Shield filename. No layout,
// so the A4 pages print clean; the toolbar is hidden from print.
// ponytail: energy manager is typed per report — no field on client holds it yet.

const PRINT_CSS = `
@page { size: A4; margin: 0; }
@media print {
  body { background: #fff !important; }
  /* The app shell header renders outside this page; print only the report. */
  body * { visibility: hidden; }
  .nta-report, .nta-report * { visibility: visible; }
  .nta-report { position: absolute; left: 0; top: 0; }
  .nta-no-print { display: none !important; }
  * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
}`;

const EMPTY_MANAGER: NtaEnergyManager = { name: '', title: '', phone: '', mobile: '', email: '' };

export const NtaReportPage = () => {
  const [params] = useSearchParams();
  const runId = params.get('run') ?? '';
  const clientId = params.get('client') ?? '';
  const [client, setClient] = useState<{ id: string; name: string | null } | null>(null);
  const [run, setRun] = useState<NtaRun | null>(null);
  const [results, setResults] = useState<NtaResultRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [manager, setManager] = useState<NtaEnergyManager>(EMPTY_MANAGER);
  const [accountRef, setAccountRef] = useState('');
  // Spec 8.1 / C-01: no NMIs on the client report unless asked for.
  const [showNmis, setShowNmis] = useState(false);

  useEffect(() => {
    if (!runId || !clientId) { setError('run and client are required'); setLoading(false); return; }
    let mounted = true;
    const load = async () => {
      setLoading(true);
      const res = await ntaService.getClientReport(runId, clientId);
      if (!mounted) return;
      if (!res.success) setError(res.error || 'Failed to load report data');
      setClient(res.data?.client ?? null);
      setRun(res.data?.run ?? null);
      setResults(res.data?.results ?? []);
      setLoading(false);
    };
    void load();
    return () => { mounted = false; };
  }, [runId, clientId]);

  const plan = useMemo(() => planClientReport(results), [results]);
  const printable = plan.variant === 'recommendation' || plan.variant === 'no_recommendation';
  const period = run ? ntaPeriodLabel(run.run_date) : '';
  const customerName = client?.name ?? '';
  const filename = ntaReportFilename(period, accountRef, customerName);

  useEffect(() => {
    if (!customerName) return;
    const prev = document.title;
    document.title = filename; // browsers use the title as the Save-as-PDF filename
    return () => { document.title = prev; };
  }, [filename, customerName]);

  const field = (key: keyof NtaEnergyManager, label: string) => (
    <label className="text-xs text-gray-600">
      {label}
      <input
        className="mt-1 block w-44 rounded border px-2 py-1 text-sm"
        value={manager[key]}
        onChange={(e) => setManager({ ...manager, [key]: e.target.value })}
      />
    </label>
  );

  return (
    <div className="min-h-screen bg-gray-100">
      <style>{PRINT_CSS}</style>
      <div className="nta-no-print sticky top-0 z-10 border-b bg-white px-6 py-3 shadow-sm">
        <div className="flex flex-wrap items-end gap-3">
          <div className="mr-4">
            <Link to="/bill-validation/nta" className="text-xs text-gray-500 hover:underline">← Network Tariff Analysis</Link>
            <div className="flex items-center gap-2 text-sm font-semibold text-gray-900">
              Client NTA report · {customerName || '—'} <NewBadge />
            </div>
            <div className="text-xs text-gray-500">
              {plan.variant === 'no_recommendation' ? 'No recommendation' : fmtMoney(plan.total)} · saving on{' '}
              {plan.savingNmis.length} of {plan.sites.length} NMI(s) · {period}
            </div>
          </div>
          {field('name', 'Energy manager')}
          {field('title', 'Title')}
          {field('phone', 'Phone')}
          {field('mobile', 'Mobile')}
          {field('email', 'Email')}
          <label className="text-xs text-gray-600">
            Account ref
            <input className="mt-1 block w-32 rounded border px-2 py-1 text-sm" value={accountRef}
              onChange={(e) => setAccountRef(e.target.value)} />
          </label>
          <label className="flex items-center gap-1 text-xs text-gray-600">
            <input type="checkbox" checked={showNmis} onChange={(e) => setShowNmis(e.target.checked)} />
            Show NMIs on report
          </label>
          <Button size="sm" className="ml-auto" onClick={() => window.print()} disabled={loading || !!error || !printable}>
            <Printer className="h-4 w-4" /> Print / Save PDF
          </Button>
        </div>
        <div className="mt-1 text-xs text-gray-400">File name: {filename}.pdf</div>
      </div>

      {loading && <p className="p-6 text-sm text-gray-500">Loading report…</p>}
      {error && (
        <div className="nta-no-print m-6 flex items-center gap-2 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          <AlertCircle className="h-4 w-4" /> {error}
        </div>
      )}
      {!loading && !error && plan.variant === 'empty' && (
        <div className="nta-no-print m-6 rounded border bg-white px-3 py-2 text-sm text-gray-600">
          No NTA results for this customer in this run.
        </div>
      )}
      {!loading && !error && plan.variant === 'pending' && (
        <div className="nta-no-print m-6 rounded border bg-white px-3 py-2 text-sm text-gray-600">
          Some of this customer&apos;s NMIs are still being assessed. The report is available once the run has finished.
        </div>
      )}
      {!loading && !error && plan.variant === 'no_recommendation' && (
        <div className="nta-no-print mx-6 mt-6 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          No NMI has a saving: this is the no-recommendation report. Wording is a draft for Choice Energy to confirm (D-06).
        </div>
      )}
      {!loading && !error && printable && (
        <div className="nta-sheet mx-auto my-6 w-fit shadow-lg print:my-0">
          {plan.variant === 'no_recommendation' ? (
            <NtaReport customerName={customerName} savings={0} energyManager={manager} variant="no_recommendation"
              sites={plan.sites} showNmis={showNmis} />
          ) : (
            <NtaReport customerName={customerName} savings={plan.total} energyManager={manager}
              note={showNmis ? mixedNote(plan.noChangeSites) : null} />
          )}
        </div>
      )}
    </div>
  );
};
